/**
 * NewTrainerOverviewPage — production version of the trainer overview.
 * Route: /training/new-trainer-overview
 * - Real data via GET /api/training/reports/new-trainer-overview
 * - Multi-select for batch state
 * - Timeline chips: Last month, This month, Choose month, Choose quarter, Custom dates + FY 2026 shortcuts
 * - Include / Exclude filters for Trainer and Category (Zoho branch)
 * - Modal drill-down chain: Trainer -> Batch -> Student
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertTriangle, ArrowUpDown, ChevronRight, Users, GraduationCap, Layers,
  ExternalLink, Filter, Sparkles, Loader2, RotateCcw, Check, CalendarDays,
} from 'lucide-react';
import api from '../../lib/api';

/* ─── utils ─────────────────────────────────────────────────── */
const inr = (n) => n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`;
const inrFull = (n) => n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`;
// Show '—' when a metric is 0 due to missing source data (no attendance /
// PDP / mock records yet) so the UI doesn't read as a genuine 0% failure.
const pct = (n) => (n == null || n === 0) ? '—' : `${Math.round(n)}%`;

const STATE_META = {
  running: { label: 'Running', tone: 'emerald', short: 'R' },
  upcoming: { label: 'Upcoming', tone: 'blue', short: 'U' },
  completed: { label: 'Completed', tone: 'slate', short: 'C' },
  on_hold: { label: 'On Hold', tone: 'amber', short: 'H' },
  unknown: { label: 'Unclassified', tone: 'slate', short: '?' },
};

const TONE_MAP = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
};

/* ─── Timeline builder ──────────────────────────────────────── */
const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const monthLabel = (d) => d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

function buildRange(preset, custom) {
  const today = new Date();
  if (preset === 'this_month') return { from: toISO(startOfMonth(today)), to: toISO(endOfMonth(today)), label: `${monthLabel(today)}` };
  if (preset === 'last_month') {
    const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { from: toISO(startOfMonth(d)), to: toISO(endOfMonth(d)), label: monthLabel(d) };
  }
  if (preset === 'fy26') return { from: '2026-04-01', to: '2027-03-31', label: 'FY 2026' };
  if (preset === 'fy25') return { from: '2025-04-01', to: '2026-03-31', label: 'FY 2025' };
  if (preset === 'ytd') return { from: `${today.getFullYear()}-01-01`, to: toISO(today), label: `YTD ${today.getFullYear()}` };
  if (preset === 'all') return { from: '', to: '', label: 'All time' };
  if (preset === 'month' && custom?.month) {
    const [y, m] = custom.month.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return { from: toISO(startOfMonth(d)), to: toISO(endOfMonth(d)), label: monthLabel(d) };
  }
  if (preset === 'quarter' && custom?.quarter) {
    const [y, q] = custom.quarter.split('-Q').map(Number);
    const startMonth = (q - 1) * 3;
    const from = new Date(y, startMonth, 1);
    const to = new Date(y, startMonth + 3, 0);
    return { from: toISO(from), to: toISO(to), label: `Q${q} ${y}` };
  }
  if (preset === 'custom' && custom?.from && custom?.to) return { from: custom.from, to: custom.to, label: `${custom.from} → ${custom.to}` };
  return { from: '2026-04-01', to: '2027-03-31', label: 'FY 2026' };
}

/* ─── STATE STRIP ────────────────────────────────────────── */
function StateStrip({ counts }) {
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
  if (!total) return <span className="text-[10px] text-slate-300 italic">—</span>;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {Object.entries(counts).map(([k, n]) => {
        const m = STATE_META[k] || { label: k, tone: 'slate', short: '?' };
        return (
          <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${TONE_MAP[m.tone]}`} title={`${m.label}: ${n}`}>
            {n}{m.short}
          </span>
        );
      })}
    </div>
  );
}

/* ─── Date Filter Popover ─────────────────────────────────── */
function DateFilterPopover({ timelinePreset, setTimelinePreset, customMonth, setCustomMonth, customQuarter, setCustomQuarter, customDates, setCustomDates, rangeLabel }) {
  const [open, setOpen] = useState(false);
  const active = timelinePreset !== 'all';
  const clearPickers = () => { setCustomMonth(''); setCustomQuarter(''); setCustomDates({ from: '', to: '' }); };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${active ? 'bg-violet-50 text-violet-700 border-violet-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
        data-testid="nto-datefilter">
        <CalendarDays className="w-3 h-3" /> Started in: <b>{rangeLabel}</b>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-xl p-3 w-[360px]" data-testid="nto-datefilter-menu">
            <p className="text-[10px] uppercase font-semibold text-slate-400 mb-2">Batch start date window</p>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {[['all', 'All time'], ['this_month', 'This month'], ['last_month', 'Last month'], ['fy26', 'FY 26'], ['fy25', 'FY 25'], ['ytd', 'YTD']].map(([k, l]) => (
                <button key={k} onClick={() => { setTimelinePreset(k); clearPickers(); }}
                  className={`px-2 py-1 rounded-md border text-[11px] ${timelinePreset === k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`nto-timeline-${k}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-1.5">
              <label className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md border text-[11px] ${timelinePreset === 'month' ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200'}`}>
                <span className="text-slate-500 font-medium">Choose month</span>
                <input type="month" value={customMonth}
                  onChange={(e) => { setCustomMonth(e.target.value); setTimelinePreset('month'); setCustomQuarter(''); setCustomDates({ from: '', to: '' }); }}
                  className="text-[10px] bg-transparent focus:outline-none" data-testid="nto-month-picker" />
              </label>
              <label className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md border text-[11px] ${timelinePreset === 'quarter' ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200'}`}>
                <span className="text-slate-500 font-medium">Choose quarter</span>
                <select value={customQuarter}
                  onChange={(e) => { setCustomQuarter(e.target.value); setTimelinePreset('quarter'); setCustomMonth(''); setCustomDates({ from: '', to: '' }); }}
                  className="text-[10px] bg-transparent focus:outline-none" data-testid="nto-quarter-picker">
                  <option value="">Select…</option>
                  {['2026-Q1','2026-Q2','2026-Q3','2026-Q4','2025-Q1','2025-Q2','2025-Q3','2025-Q4'].map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <div className={`flex items-center justify-between gap-1 px-2 py-1 rounded-md border text-[11px] ${timelinePreset === 'custom' ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200'}`}>
                <span className="text-slate-500 font-medium">Custom range</span>
                <div className="flex items-center gap-1">
                  <input type="date" value={customDates.from}
                    onChange={(e) => { setCustomDates(v => ({ ...v, from: e.target.value })); setTimelinePreset('custom'); setCustomMonth(''); setCustomQuarter(''); }}
                    className="text-[10px] bg-transparent focus:outline-none" data-testid="nto-custom-from" />
                  <span>→</span>
                  <input type="date" value={customDates.to}
                    onChange={(e) => { setCustomDates(v => ({ ...v, to: e.target.value })); setTimelinePreset('custom'); setCustomMonth(''); setCustomQuarter(''); }}
                    className="text-[10px] bg-transparent focus:outline-none" data-testid="nto-custom-to" />
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mt-3 pt-2 border-t border-slate-100">
              <span className="text-[10px] text-slate-500">Current: <b>{rangeLabel}</b></span>
              <button onClick={() => { setTimelinePreset('all'); clearPickers(); }}
                className="text-[10px] text-rose-600 hover:underline font-medium" data-testid="nto-datefilter-clear">Clear date filter</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Include/Exclude multiselect popover ───────────────────── */
function IncExcPopover({ label, options, include, exclude, setInclude, setExclude, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const active = include.length + exclude.length;
  const filtered = useMemo(() => options.filter(o => o.toLowerCase().includes(q.toLowerCase())), [options, q]);
  const state = (o) => include.includes(o) ? 'inc' : exclude.includes(o) ? 'exc' : 'off';
  const cycle = (o) => {
    const s = state(o);
    if (s === 'off') {
      setInclude([...include, o]);
    } else if (s === 'inc') {
      setInclude(include.filter(x => x !== o));
      setExclude([...exclude, o]);
    } else {
      setExclude(exclude.filter(x => x !== o));
    }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${active ? 'bg-violet-50 text-violet-700 border-violet-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
        data-testid={testid}>
        <Filter className="w-3 h-3" /> {label}{active > 0 && <span className="ml-1 bg-violet-600 text-white px-1 py-0 rounded text-[9px]">{active}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-xl w-[280px] max-h-[380px] flex flex-col" data-testid={`${testid}-menu`}>
            <div className="p-2 border-b border-slate-100">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full text-[11px] px-2 py-1 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-400" data-testid={`${testid}-search`} />
              <div className="flex justify-between items-center mt-1 text-[10px] text-slate-500">
                <span>OFF → Include → Exclude → OFF</span>
                {active > 0 && <button onClick={() => { setInclude([]); setExclude([]); }} className="text-rose-600 hover:underline font-medium" data-testid={`${testid}-clear`}>Clear</button>}
              </div>
            </div>
            <div className="flex-1 overflow-auto py-1">
              {filtered.length === 0 && <p className="text-[10px] italic text-slate-400 px-2 py-2">No matches</p>}
              {filtered.map(o => {
                const s = state(o);
                return (
                  <button key={o} onClick={() => cycle(o)} className="w-full flex items-center justify-between px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`${testid}-opt-${o}`}>
                    <span className="truncate text-slate-700">{o}</span>
                    {s === 'inc' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">Include ✓</Badge>}
                    {s === 'exc' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[9px]">Exclude ✕</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── STUDENT 360° ─────────────────────────────────────────── */
function StudentModal({ student, batch, open, onClose }) {
  if (!student) return null;
  const ZOHO_ORG_ID = '699857543';
  const crmUrl = student.zoho_customer_id ? `https://crm.zoho.in/crm/org60036129527/tab/Contacts/${student.zoho_customer_id}` : null;
  const booksUrl = student.zoho_customer_id ? `https://books.zoho.com/app/${ZOHO_ORG_ID}/#/customers/${student.zoho_customer_id}` : null;
  const gross = student.total_amount || 0;
  const paid = student.effective_paid != null ? student.effective_paid : (student.paid_amount || 0);
  const due = student.effective_due != null ? student.effective_due : (student.due_amount || 0);
  const cn = student.credit_applied_amount || student.credit_amount || 0;
  const wo = student.write_off_amount || 0;
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[130px_1fr] gap-x-4 py-1 text-[12px] items-baseline">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-800 font-medium">{children || <span className="text-slate-300 italic font-normal">—</span>}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="student-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-violet-600" />
              <span className="text-slate-400 font-normal text-sm">Enrol #{student.enrollment_no || '—'} ·</span>
              <span>{student.name}</span>
            </span>
            <div className="flex items-center gap-2 text-[11px]">
              {crmUrl && <a href={crmUrl} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-1"><ExternalLink className="w-3 h-3" />Open in CRM</a>}
              {booksUrl && <a href={booksUrl} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline flex items-center gap-1">View in Books <ExternalLink className="w-3 h-3" /></a>}
            </div>
          </DialogTitle>
          <p className="text-[11px] text-slate-500 mt-1">Batch #{batch?.batch_number} · {batch?.course_name} · Trainer {batch?.trainer_name}</p>
        </DialogHeader>

        {/* Key-value list (matches /training/students overview) */}
        <div className="border-b border-slate-100 pb-3">
          <Row label="Enrollment No">{student.enrollment_no}</Row>
          <Row label="Name">{student.name}</Row>
          <Row label="Phone">{student.phone}</Row>
          <Row label="Email">{student.email}</Row>
          <Row label="Course">{student.course}</Row>
          <Row label="Branch">{student.branch}</Row>
          <Row label="Counsellor">{student.salesperson}</Row>
          <Row label="Source">
            {student.zoho_customer_id && (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded bg-blue-50 text-blue-700 border border-blue-200 font-normal">Zoho Books</span>
            )}
          </Row>
        </div>

        {/* Financial Summary card */}
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-2">Financial Summary</p>
          <div className="grid grid-cols-4 gap-3 py-3 border border-slate-200 rounded-lg">
            <div className="text-center">
              <p className="text-[10px] text-slate-400 uppercase">Total</p>
              <p className="text-xl font-bold text-slate-800 mt-1">{inrFull(gross)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-slate-400 uppercase">Paid</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">{inrFull(paid)}</p>
              {cn > 0 && <p className="text-[9px] text-amber-600 mt-0.5">incl. CN {inrFull(cn)}</p>}
            </div>
            <div className="text-center">
              <p className="text-[10px] text-slate-400 uppercase">Write Off</p>
              <p className="text-xl font-bold text-slate-500 mt-1">{wo > 0 ? inrFull(wo) : '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-slate-400 uppercase">Due</p>
              <p className={`text-xl font-bold mt-1 ${due > 0 ? 'text-rose-600' : 'text-slate-500'}`}>{inrFull(due)}</p>
            </div>
          </div>
        </div>

        {/* Training metrics */}
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Training</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">PDP</p><p className="text-sm font-bold text-slate-700">{student.pdp_status || '—'}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Mock</p><p className="text-sm font-bold text-slate-700">{student.mock_status || '—'}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Placement</p><p className="text-sm font-bold text-slate-700">{student.placement_status || '—'}</p></Card>
          </div>
        </div>

        {/* Invoices */}
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Invoices ({(student.invoices || []).length})</p>
          {(student.invoices || []).length === 0 ? (
            <p className="text-[10px] italic text-slate-400">No invoices linked</p>
          ) : (
            (student.invoices || []).map((inv) => {
              const statusTone = ({
                paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                partially_paid: 'bg-blue-50 text-blue-700 border-blue-200',
                overdue: 'bg-rose-50 text-rose-700 border-rose-200',
                sent: 'bg-slate-50 text-slate-600 border-slate-200',
                void: 'bg-amber-50 text-amber-700 border-amber-200',
              })[(inv.status || '').toLowerCase()] || 'bg-slate-50 text-slate-600 border-slate-200';
              return (
                <a key={inv.invoice_id} href={`https://books.zoho.com/app/${ZOHO_ORG_ID}/#/invoices/${inv.invoice_id}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50 mt-1 text-[11px] hover:bg-violet-50 group border border-slate-100"
                  data-testid={`nto-student-inv-${inv.invoice_id}`}>
                  <span className="font-mono font-medium text-slate-700 group-hover:text-violet-700 flex-1 truncate">{inv.invoice_number || '—'}</span>
                  <span className="text-slate-500">{inv.date || ''}</span>
                  <span className="font-semibold text-slate-700">{inrFull(inv.total)}</span>
                  {(inv.credit_applied || 0) > 0 && <span className="text-amber-700 text-[10px]">CN {inrFull(inv.credit_applied)}</span>}
                  {(inv.effective_due || 0) > 0 && <span className="text-rose-600 text-[10px]">Due {inrFull(inv.effective_due)}</span>}
                  <Badge className={`text-[9px] border capitalize ${statusTone}`}>{(inv.status || '').replace('_', ' ') || '—'}</Badge>
                  <ExternalLink className="w-2.5 h-2.5 text-violet-500" />
                </a>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── BATCH MODAL ────────────────────────────────────────── */
function BatchModal({ batch, open, onClose }) {
  const [sel, setSel] = useState(null);
  if (!batch) return null;
  const st = STATE_META[batch.state] || STATE_META.completed;
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="batch-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Layers className="w-4 h-4 text-violet-600" />
              Batch #{batch.batch_number} · {batch.course_name}
              <Badge className={`ml-1 border text-[10px] ${TONE_MAP[st.tone]}`}>{st.label}</Badge>
            </DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              <span>Trainer <b className="text-slate-700">{batch.trainer_name}</b></span>
              <span>{(batch.batch_type || '').replace(/[()]/g, '').trim()}</span>
              <span>Centre {batch.centre_name}</span>
              <span>{batch.start_date} → {batch.end_date}</span>
              {batch.whatsapp_link_crm && <a href={batch.whatsapp_link_crm} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">WhatsApp ↗</a>}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Gross</p><p className="text-sm font-bold text-slate-700">{inr(batch.gross_inr)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Paid</p><p className="text-sm font-bold text-emerald-700">{inr(batch.paid_inr)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Due</p><p className="text-sm font-bold text-rose-700">{inr(batch.due_inr)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Students</p><p className="text-sm font-bold text-slate-700">{batch.student_count}</p></Card>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2 text-center">
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Attendance</p><p className="text-sm font-bold text-slate-700">{pct(batch.attendance_pct)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">PDP</p><p className="text-sm font-bold text-slate-700">{pct(batch.pdp_pct)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Mock</p><p className="text-sm font-bold text-slate-700">{pct(batch.mock_pct)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Placement Ready</p><p className="text-sm font-bold text-slate-700">{pct(batch.placement_ready_pct)}</p></div>
          </div>
          <div className="mt-3 flex-1 overflow-auto">
            <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1">Students ({(batch.students || []).length})</p>
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 border-y sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1.5 font-medium">Name</th>
                  <th className="px-2 py-1.5 font-medium">Enrol#</th>
                  <th className="px-2 py-1.5 font-medium text-right">Gross</th>
                  <th className="px-2 py-1.5 font-medium text-right">Paid</th>
                  <th className="px-2 py-1.5 font-medium text-right">Due</th>
                  <th className="px-2 py-1.5 font-medium">PDP</th>
                  <th className="px-2 py-1.5 font-medium">Mock</th>
                  <th className="px-2 py-1.5 font-medium">Placement</th>
                  <th className="px-2 py-1.5 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {(batch.students || []).map(s => (
                  <tr key={s.id} onClick={() => setSel(s)}
                    className="border-b border-slate-100 hover:bg-violet-50/50 cursor-pointer" data-testid={`nto-student-row-${s.id}`}>
                    <td className="px-2 py-1.5 text-slate-700 font-medium">{s.name}</td>
                    <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">{s.enrollment_no}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{inrFull(s.total_amount)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700">{inrFull(s.paid_amount)}</td>
                    <td className="px-2 py-1.5 text-right text-rose-700">{s.due_amount ? inrFull(s.due_amount) : '—'}</td>
                    <td className="px-2 py-1.5 text-[9px] text-slate-500">{s.pdp_status || '—'}</td>
                    <td className="px-2 py-1.5 text-[9px] text-slate-500">{s.mock_status || '—'}</td>
                    <td className="px-2 py-1.5 text-[10px] text-slate-600">{s.placement_status || '—'}</td>
                    <td className="px-2 py-1.5 text-slate-300"><ChevronRight className="w-3 h-3" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      <StudentModal student={sel} batch={batch} open={!!sel} onClose={() => setSel(null)} />
    </>
  );
}

/* ─── TRAINER MODAL ───────────────────────────────────────── */
function TrainerModal({ trainer, batches, kpi, open, onClose }) {
  const [sel, setSel] = useState(null);
  if (!trainer) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="trainer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4 text-violet-600" /> {trainer}
            </DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">
              {batches.length} batches · {kpi?.students || 0} students · <b className="text-slate-700">{inr(kpi?.gross)} gross</b>
              {(kpi?.counts?.overdue || 0) > 0 && <span className="ml-2 text-rose-600">⚠ {kpi.counts.overdue} overdue</span>}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Gross</p><p className="text-sm font-bold text-slate-700">{inr(kpi?.gross)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Paid</p><p className="text-sm font-bold text-emerald-700">{inr(kpi?.paid)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Due</p><p className="text-sm font-bold text-rose-700">{inr(kpi?.due)}</p></Card>
            <Card className="p-2 border">
              <p className="text-[9px] text-slate-400 uppercase">Audit adj.</p>
              <p className="text-sm font-bold text-amber-700">{inr((kpi?.credit_note || 0) + (kpi?.write_off || 0))}</p>
              <p className="text-[9px] text-slate-400">CN {inr(kpi?.credit_note)} · WO {inr(kpi?.write_off)}</p>
            </Card>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2 text-center">
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Attendance</p><p className="text-sm font-bold text-slate-700">{pct(kpi?.attendance)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">PDP</p><p className="text-sm font-bold text-slate-700">{pct(kpi?.pdp)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Mock</p><p className="text-sm font-bold text-slate-700">{pct(kpi?.mock)}</p></div>
            <div className="p-2 border rounded"><p className="text-[9px] text-slate-400 uppercase">Placement</p><p className="text-sm font-bold text-slate-700">{pct(kpi?.placement)}</p></div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">State mix:<StateStrip counts={kpi?.counts} /></div>
          <div className="mt-3 flex-1 overflow-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 border-y sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1.5 font-medium">Batch</th>
                  <th className="px-2 py-1.5 font-medium">Course</th>
                  <th className="px-2 py-1.5 font-medium">Type</th>
                  <th className="px-2 py-1.5 font-medium">Centre</th>
                  <th className="px-2 py-1.5 font-medium">State</th>
                  <th className="px-2 py-1.5 font-medium">Start</th>
                  <th className="px-2 py-1.5 font-medium text-right">Stds</th>
                  <th className="px-2 py-1.5 font-medium text-right">Gross</th>
                  <th className="px-2 py-1.5 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => {
                  const s = STATE_META[b.state] || STATE_META.completed;
                  return (
                    <tr key={b.batch_id} onClick={() => setSel(b)}
                      className="border-b border-slate-100 hover:bg-violet-50/50 cursor-pointer" data-testid={`nto-batch-row-${b.batch_id}`}>
                      <td className="px-2 py-1.5 font-semibold text-violet-600">#{b.batch_number}</td>
                      <td className="px-2 py-1.5 text-slate-700">{b.course_name}</td>
                      <td className="px-2 py-1.5 text-slate-500 text-[10px]">{(b.batch_type || '').includes('Weekend') ? 'Weekend' : 'Weekday'}</td>
                      <td className="px-2 py-1.5 text-slate-500">{b.centre_name}</td>
                      <td className="px-2 py-1.5"><Badge className={`text-[10px] border ${TONE_MAP[s.tone]}`}>{s.label}</Badge></td>
                      <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">{b.start_date}</td>
                      <td className="px-2 py-1.5 text-right text-slate-700">{b.student_count}</td>
                      <td className="px-2 py-1.5 text-right text-slate-700 font-medium">{inr(b.gross_inr)}</td>
                      <td className="px-2 py-1.5 text-slate-300"><ChevronRight className="w-3 h-3" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      <BatchModal batch={sel} open={!!sel} onClose={() => setSel(null)} />
    </>
  );
}

/* ─── CATEGORY MODAL ─────────────────────────────────────── */
function CategoryModal({ category, batches, kpi, open, onClose }) {
  const [sel, setSel] = useState(null);
  const byTrainer = useMemo(() => {
    if (!category) return [];
    const m = {};
    batches.forEach(b => { (m[b.trainer_name] = m[b.trainer_name] || []).push(b); });
    return Object.entries(m).map(([name, bs]) => ({
      trainer: name,
      batches: bs,
      students: bs.reduce((a, b) => a + b.student_count, 0),
      gross: bs.reduce((a, b) => a + b.gross_inr, 0),
      paid: bs.reduce((a, b) => a + b.paid_inr, 0),
      due: bs.reduce((a, b) => a + b.due_inr, 0),
      credit_note: bs.reduce((a, b) => a + b.credit_note_inr, 0),
      write_off: bs.reduce((a, b) => a + b.write_off_inr, 0),
      attendance: Math.round(bs.reduce((a, b) => a + b.attendance_pct, 0) / bs.length),
      pdp: Math.round(bs.reduce((a, b) => a + b.pdp_pct, 0) / bs.length),
      mock: Math.round(bs.reduce((a, b) => a + b.mock_pct, 0) / bs.length),
      placement: Math.round(bs.reduce((a, b) => a + b.placement_ready_pct, 0) / bs.length),
      counts: bs.reduce((a, b) => { a[b.state] = (a[b.state] || 0) + 1; return a; }, {}),
    })).sort((a, b) => b.gross - a.gross);
  }, [batches, category]);
  if (!category) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Sparkles className="w-4 h-4 text-violet-600" />{category}</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">{batches.length} batches · {byTrainer.length} trainers · {kpi?.students} students · <b className="text-slate-700">{inr(kpi?.gross)} gross</b></div>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Gross</p><p className="text-sm font-bold text-slate-700">{inr(kpi?.gross)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Paid</p><p className="text-sm font-bold text-emerald-700">{inr(kpi?.paid)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Due</p><p className="text-sm font-bold text-rose-700">{inr(kpi?.due)}</p></Card>
            <Card className="p-2 border"><p className="text-[9px] text-slate-400 uppercase">Trainers</p><p className="text-sm font-bold text-slate-700">{byTrainer.length}</p></Card>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">State mix:<StateStrip counts={kpi?.counts} /></div>
          <div className="mt-3 flex-1 overflow-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 border-y sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1.5 font-medium">Trainer</th>
                  <th className="px-2 py-1.5 font-medium text-right">Batches</th>
                  <th className="px-2 py-1.5 font-medium text-right">Students</th>
                  <th className="px-2 py-1.5 font-medium text-right">Gross</th>
                  <th className="px-2 py-1.5 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {byTrainer.map(r => (
                  <tr key={r.trainer} onClick={() => setSel(r)}
                    className="border-b border-slate-100 hover:bg-violet-50/50 cursor-pointer" data-testid={`nto-cat-trainer-row-${r.trainer}`}>
                    <td className="px-2 py-1.5 text-slate-700 font-medium">{r.trainer}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{r.batches.length}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{r.students}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700 font-medium">{inr(r.gross)}</td>
                    <td className="px-2 py-1.5 text-slate-300"><ChevronRight className="w-3 h-3" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      {sel && <TrainerModal trainer={sel.trainer} batches={sel.batches} kpi={sel} open={!!sel} onClose={() => setSel(null)} />}
    </>
  );
}

/* ─── AUDIT DETAIL MODAL — student + batch + invoice + CRM/Books links ─── */
function AuditDetailModal({ record, kind, open, onClose }) {
  if (!record) return null;
  const ZOHO_ORG_ID = '699857543';
  const CRM_ORG_ID = 'org60036129527';
  const isCN = kind === 'cn';
  const cnUrl = record.creditnote_id ? `https://books.zoho.com/app/${ZOHO_ORG_ID}/#/creditnotes/${record.creditnote_id}` : null;
  const invUrl = record.invoice_id ? `https://books.zoho.com/app/${ZOHO_ORG_ID}/#/invoices/${record.invoice_id}` : null;
  const bookCustomerUrl = record.zoho_customer_id ? `https://books.zoho.com/app/${ZOHO_ORG_ID}/#/customers/${record.zoho_customer_id}` : null;
  const crmLeadUrl = record.zoho_customer_id ? `https://crm.zoho.in/crm/${CRM_ORG_ID}/tab/Leads/${record.zoho_customer_id}` : null;
  const crmContactUrl = record.zoho_customer_id ? `https://crm.zoho.in/crm/${CRM_ORG_ID}/tab/Contacts/${record.zoho_customer_id}` : null;
  const crmBatchUrl = record.batch_id ? `/training/batches/${record.batch_id}` : null;
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[130px_1fr] gap-x-4 py-1 text-[12px] items-baseline">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-800 font-medium">{children || <span className="text-slate-300 italic font-normal">—</span>}</span>
    </div>
  );
  const LinkBtn = ({ href, children, tone = 'violet', testid }) => href ? (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] hover:brightness-105 ${
        tone === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-300'
        : tone === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-300'
        : 'bg-violet-50 text-violet-700 border-violet-300'}`}>
      <ExternalLink className="w-3 h-3" /> {children}
    </a>
  ) : null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="audit-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className={`w-4 h-4 ${isCN ? 'text-amber-600' : 'text-slate-500'}`} />
            {isCN ? `Credit Note · ${record.creditnote_number}` : `Write-off · ${record.student_name}`}
            {isCN && <Badge className="ml-1 bg-amber-50 text-amber-700 border-amber-200 text-[10px] capitalize">{record.status}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {/* Amount headline */}
        <div className="grid grid-cols-3 gap-3 py-3 border border-slate-200 rounded-lg my-2">
          {isCN ? (
            <>
              <div className="text-center"><p className="text-[10px] uppercase text-slate-400">Amount applied</p><p className="text-xl font-bold text-amber-700 mt-1">{inrFull(record.amount)}</p></div>
              <div className="text-center"><p className="text-[10px] uppercase text-slate-400">CN Total</p><p className="text-xl font-bold text-slate-700 mt-1">{inrFull(record.total)}</p></div>
              <div className="text-center"><p className="text-[10px] uppercase text-slate-400">Credits Applied</p><p className="text-xl font-bold text-slate-700 mt-1">{inrFull(record.credits_applied)}</p></div>
            </>
          ) : (
            <div className="col-span-3 text-center"><p className="text-[10px] uppercase text-slate-400">Write-off amount</p><p className="text-2xl font-bold text-amber-700 mt-1">{inrFull(record.amount)}</p></div>
          )}
        </div>

        {/* Student */}
        <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1 mt-1">Student</p>
        <div className="border-t border-slate-100 pt-1">
          <Row label="Name">{record.student_name}</Row>
          <Row label="Enrollment No">{record.enrollment_no}</Row>
          <Row label="Phone">{record.phone}</Row>
          <Row label="Email">{record.email}</Row>
          <Row label="Course">{record.course}</Row>
          <Row label="Branch">{record.branch}</Row>
          <Row label="Counsellor">{record.salesperson}</Row>
        </div>

        {/* Batch */}
        <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1 mt-3">Batch</p>
        <div className="border-t border-slate-100 pt-1">
          <Row label="Batch #">{record.batch_number}</Row>
          <Row label="Course">{record.course_name}</Row>
          <Row label="Trainer">{record.trainer_name}</Row>
        </div>

        {/* Invoice (CN only) */}
        {isCN && (
          <>
            <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1 mt-3">Invoice</p>
            <div className="border-t border-slate-100 pt-1">
              <Row label="Invoice #">{record.invoice_number}</Row>
              <Row label="Invoice total">{inrFull(record.invoice_total)}</Row>
              <Row label="Invoice status"><Badge className="text-[10px] capitalize bg-slate-50 border">{(record.invoice_status || '').replace('_', ' ') || '—'}</Badge></Row>
            </div>
          </>
        )}

        {/* Links */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-slate-100">
          {isCN && <LinkBtn href={cnUrl} tone="amber" testid="audit-detail-cn-link">Open CN in Zoho Books</LinkBtn>}
          {isCN && <LinkBtn href={invUrl} testid="audit-detail-invoice-link">Open Invoice in Books</LinkBtn>}
          <LinkBtn href={bookCustomerUrl} testid="audit-detail-books-customer">Zoho Books Customer</LinkBtn>
          <LinkBtn href={crmContactUrl} tone="blue" testid="audit-detail-crm-contact">CRM Contact</LinkBtn>
          <LinkBtn href={crmLeadUrl} tone="blue" testid="audit-detail-crm-lead">CRM Lead</LinkBtn>
          {crmBatchUrl && <a href={crmBatchUrl} target="_blank" rel="noreferrer" data-testid="audit-detail-batch-link"
            className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] bg-violet-50 text-violet-700 border-violet-300">
            <ExternalLink className="w-3 h-3" /> Open Batch
          </a>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── AUDIT MODAL ───────────────────────────────────────── */
function AuditModal({ audit, open, onClose }) {
  const [sel, setSel] = useState(null); // {kind, record}
  const cns = audit?.credit_notes || [];
  const wos = audit?.write_offs || [];
  const totalCN = cns.reduce((a, b) => a + (b.amount || 0), 0);
  const totalWO = wos.reduce((a, b) => a + (b.amount || 0), 0);
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="audit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><AlertTriangle className="w-4 h-4 text-amber-600" />Audit adjustments · click a row for full details</DialogTitle>
            <p className="text-[10px] text-slate-500 mt-1">Void payments are excluded upstream in Books Analytics. Credit notes and write-offs surfaced here are already netted off from the Paid total.</p>
          </DialogHeader>
          <div className="mt-2 flex-1 overflow-auto">
            <p className="text-[11px] font-semibold text-slate-700 mb-1">Credit notes · {inrFull(totalCN)} ({cns.length})</p>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1.5 font-medium">CN #</th>
                    <th className="px-2 py-1.5 font-medium">Date</th>
                    <th className="px-2 py-1.5 font-medium text-right">Amount</th>
                    <th className="px-2 py-1.5 font-medium">Invoice</th>
                    <th className="px-2 py-1.5 font-medium">Student</th>
                    <th className="px-2 py-1.5 font-medium">Batch · Trainer</th>
                    <th className="px-2 py-1.5 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {cns.length === 0 && <tr><td colSpan={7} className="p-3 italic text-slate-400 text-center">No credit notes in window</td></tr>}
                  {cns.map((cn, i) => (
                    <tr key={`cn-${i}`} onClick={() => setSel({ kind: 'cn', record: cn })}
                      className="border-b border-slate-100 hover:bg-violet-50 cursor-pointer" data-testid={`nto-audit-cn-${cn.creditnote_id}`}>
                      <td className="px-2 py-1.5 font-mono font-medium text-violet-600">{cn.creditnote_number}</td>
                      <td className="px-2 py-1.5 text-slate-500">{cn.date}</td>
                      <td className="px-2 py-1.5 text-right text-amber-700 font-semibold">{inrFull(cn.amount)}</td>
                      <td className="px-2 py-1.5 text-slate-600 font-mono text-[10px]">{cn.invoice_number}</td>
                      <td className="px-2 py-1.5 text-slate-700">
                        <div>{cn.student_name}</div>
                        <div className="text-[9px] text-slate-400">#{cn.enrollment_no}</div>
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">
                        <div>#{cn.batch_number} · {cn.course_name}</div>
                        <div className="text-[9px] text-slate-400">{cn.trainer_name}</div>
                      </td>
                      <td className="px-2 py-1.5 text-violet-500"><ChevronRight className="w-3 h-3" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] font-semibold text-slate-700 mt-4 mb-1">Write-offs · {inrFull(totalWO)} ({wos.length})</p>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1.5 font-medium">Student</th>
                    <th className="px-2 py-1.5 font-medium">Enrol#</th>
                    <th className="px-2 py-1.5 font-medium">Batch · Trainer</th>
                    <th className="px-2 py-1.5 font-medium text-right">Amount</th>
                    <th className="px-2 py-1.5 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {wos.length === 0 && <tr><td colSpan={5} className="p-3 italic text-slate-400 text-center">No write-offs in window</td></tr>}
                  {wos.map((wo, i) => (
                    <tr key={`wo-${i}`} onClick={() => setSel({ kind: 'wo', record: wo })}
                      className="border-b border-slate-100 hover:bg-violet-50 cursor-pointer" data-testid={`nto-audit-wo-${wo.student_id}`}>
                      <td className="px-2 py-1.5 text-slate-700 font-medium">{wo.student_name}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-500 text-[10px]">{wo.enrollment_no}</td>
                      <td className="px-2 py-1.5 text-slate-500">
                        <div>#{wo.batch_number} · {wo.course_name}</div>
                        <div className="text-[9px] text-slate-400">{wo.trainer_name}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-amber-700 font-semibold">{inrFull(wo.amount)}</td>
                      <td className="px-2 py-1.5 text-violet-500"><ChevronRight className="w-3 h-3" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AuditDetailModal record={sel?.record} kind={sel?.kind} open={!!sel} onClose={() => setSel(null)} />
    </>
  );
}

/* ─── MAIN PAGE ─────────────────────────────────────────── */
const DEFAULT_STATES = ['running', 'upcoming'];

export default function NewTrainerOverviewPage({ embedded = false, externalTrainer = null, externalStartRange = null, externalCategoriesInclude = null } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('trainer');
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [sort, setSort] = useState({ key: 'gross', dir: 'desc' });

  const [timelinePreset, setTimelinePreset] = useState('all');
  const [customMonth, setCustomMonth] = useState(''); // YYYY-MM
  const [customQuarter, setCustomQuarter] = useState(''); // YYYY-Q1
  const [customDates, setCustomDates] = useState({ from: '', to: '' });
  const [selectedStates, setSelectedStates] = useState(DEFAULT_STATES);
  const [selectedTypes, setSelectedTypes] = useState(['weekday', 'weekend']);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [trainerInc, setTrainerInc] = useState([]);
  const [trainerExc, setTrainerExc] = useState([]);
  const [catInc, setCatInc] = useState([]);
  const [catExc, setCatExc] = useState([]);

  const range = buildRange(timelinePreset, { month: customMonth, quarter: customQuarter, from: customDates.from, to: customDates.to });

  useEffect(() => {
    setLoading(true);
    const params = {};
    // Parent-controlled start-date window overrides the local timeline preset
    const effFrom = externalStartRange?.from ?? range.from;
    const effTo = externalStartRange?.to ?? range.to;
    if (effFrom) params.start_from = effFrom;
    if (effTo) params.start_to = effTo;
    if (selectedStates.length) params.states = selectedStates.join(',');
    if (selectedTypes.length) params.types = selectedTypes.join(',');
    const effTrainerInc = externalTrainer ? [externalTrainer] : trainerInc;
    if (effTrainerInc.length) params.trainers_include = effTrainerInc.join(',');
    if (trainerExc.length) params.trainers_exclude = trainerExc.join(',');
    const effCatInc = externalCategoriesInclude && externalCategoriesInclude.length ? externalCategoriesInclude : catInc;
    if (effCatInc.length) params.categories_include = effCatInc.join(',');
    if (catExc.length) params.categories_exclude = catExc.join(',');
    if (overdueOnly) params.overdue_only = 'true';
    api.get('/training/reports/new-trainer-overview', { params })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, selectedStates.join(','), selectedTypes.join(','), overdueOnly, trainerInc.join(','), trainerExc.join(','), catInc.join(','), catExc.join(','), externalTrainer, externalStartRange?.from, externalStartRange?.to, (externalCategoriesInclude || []).join(',')]);

  const toggleState = (k) => setSelectedStates(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  const toggleType = (k) => setSelectedTypes(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  const toggleSort = (k) => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }));
  const Th = ({ k, children, align = 'left' }) => (
    <th onClick={() => toggleSort(k)} className={`px-3 py-2 font-medium cursor-pointer hover:bg-slate-100 select-none text-${align}`} data-testid={`nto-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${sort.key === k ? 'text-violet-700' : ''}`}>{children}<ArrowUpDown className="w-3 h-3 text-slate-300" /></span>
    </th>
  );

  const p = data?.portfolio || {};
  const byTrainer = useMemo(() => {
    const rows = (data?.by_trainer || []).slice();
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => (a[sort.key === 'name' ? 'trainer_name' : sort.key] > b[sort.key === 'name' ? 'trainer_name' : sort.key] ? 1 : -1) * dir);
    return rows;
  }, [data, sort]);
  const byCategory = useMemo(() => {
    const rows = (data?.by_category || []).slice();
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => (a[sort.key === 'category' ? 'branch' : sort.key] > b[sort.key === 'category' ? 'branch' : sort.key] ? 1 : -1) * dir);
    return rows;
  }, [data, sort]);
  const trainerOptions = data?.filters?.trainers || [];
  const categoryOptions = data?.filters?.categories || [];

  const resetAll = () => {
    setTimelinePreset('all');
    setCustomMonth(''); setCustomQuarter(''); setCustomDates({ from: '', to: '' });
    setSelectedStates(DEFAULT_STATES); setSelectedTypes(['weekday', 'weekend']);
    setOverdueOnly(false);
    setTrainerInc([]); setTrainerExc([]); setCatInc([]); setCatExc([]);
    setSort({ key: 'gross', dir: 'desc' });
  };

  return (
    <div className="space-y-4 pb-8" data-testid="new-trainer-overview-page">
      {!embedded && (
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">New Trainer Overview</h1>
            <p className="text-sm text-slate-400 mt-0.5">Trainer load, batch mix &amp; revenue exposure — one screen · Started in: {range.label}</p>
          </div>
          <Button size="sm" variant="outline" onClick={resetAll} className="text-[11px]" data-testid="nto-reset">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
        </div>
      )}

      {/* FILTER BAR — hidden when embedded (parent controls filters) */}
      {!embedded && (
      <Card className="p-3 border sticky top-0 z-30 bg-white/95 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-500 font-semibold">State (multi):</span>
          {Object.entries(STATE_META).map(([k, m]) => (
            <label key={k}
              className={`flex items-center gap-1 px-2 py-1 rounded-md border cursor-pointer ${selectedStates.includes(k) ? TONE_MAP[m.tone] : 'bg-white text-slate-500 border-slate-200'}`}
              data-testid={`nto-state-${k}`}>
              <input type="checkbox" checked={selectedStates.includes(k)} onChange={() => toggleState(k)} className="hidden" />
              {selectedStates.includes(k) && <Check className="w-2.5 h-2.5" />}{m.label}
            </label>
          ))}
          <button onClick={() => setOverdueOnly(v => !v)}
            className={`px-2 py-1 rounded-md border flex items-center gap-1 ${overdueOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'}`}
            data-testid="nto-overdue-only">
            <AlertTriangle className="w-3 h-3" /> Overdue only
          </button>

          <div className="h-4 w-px bg-slate-200 mx-1" />
          <span className="text-slate-500 font-semibold">Type:</span>
          {[['weekday', 'Weekday'], ['weekend', 'Weekend']].map(([k, l]) => (
            <label key={k} className={`flex items-center gap-1 px-2 py-1 rounded-md border cursor-pointer ${selectedTypes.includes(k) ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-white text-slate-500 border-slate-200'}`}
              data-testid={`nto-type-${k}`}>
              <input type="checkbox" checked={selectedTypes.includes(k)} onChange={() => toggleType(k)} className="hidden" />
              {selectedTypes.includes(k) && <Check className="w-2.5 h-2.5" />}{l}
            </label>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <DateFilterPopover
              timelinePreset={timelinePreset} setTimelinePreset={setTimelinePreset}
              customMonth={customMonth} setCustomMonth={setCustomMonth}
              customQuarter={customQuarter} setCustomQuarter={setCustomQuarter}
              customDates={customDates} setCustomDates={setCustomDates}
              rangeLabel={range.label}
            />
            <IncExcPopover label="Trainer" options={trainerOptions} include={trainerInc} exclude={trainerExc}
              setInclude={setTrainerInc} setExclude={setTrainerExc} testid="nto-trainer-filter" />
            <IncExcPopover label="Category" options={categoryOptions} include={catInc} exclude={catExc}
              setInclude={setCatInc} setExclude={setCatExc} testid="nto-category-filter" />
          </div>
        </div>
      </Card>
      )}

      {/* PORTFOLIO */}
      <Card className="p-4 border bg-gradient-to-br from-violet-50 via-white to-slate-50">
        {loading ? (
          <div className="flex items-center justify-center h-20 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
        ) : (
          <>
            {/* Batch-status row (matches legacy Trainer Overview 42/30/13/48/297).
                Counts are universe-wide (respect trainer/category/type filters
                but ignore state chips). Click a card to toggle its state chip. */}
            {p.state_counts && (
              <div className="grid grid-cols-6 gap-2 mb-4">
                {[
                  ['weekday_running', 'Weekday Running', 'text-emerald-600', 'running', 'weekday'],
                  ['weekend_running', 'Weekend Running', 'text-emerald-600', 'running', 'weekend'],
                  ['upcoming', 'Upcoming', 'text-blue-600', 'upcoming', null],
                  ['on_hold', 'On Hold', 'text-amber-600', 'on_hold', null],
                  ['completed', 'Completed', 'text-slate-600', 'completed', null],
                  ['unknown', 'Unclassified', 'text-slate-400', 'unknown', null],
                ].map(([k, l, cls, stateKey, typeKey]) => {
                  const active = selectedStates.includes(stateKey) && (!typeKey || selectedTypes.includes(typeKey));
                  return (
                    <button key={k} onClick={() => {
                      // Toggle the state chip; if the card is type-specific,
                      // also make sure that type is enabled.
                      toggleState(stateKey);
                      if (typeKey && !selectedTypes.includes(typeKey)) toggleType(typeKey);
                    }}
                      className={`p-2 border rounded-md text-left transition-colors ${active ? 'bg-white border-violet-400 shadow-sm' : 'bg-white/60 border-slate-200 hover:bg-white'}`}
                      data-testid={`nto-kpi-${k}`}>
                      <p className={`text-2xl font-bold ${cls}`}>{p.state_counts[k] || 0}</p>
                      <p className="text-[9px] uppercase tracking-wide text-slate-500 font-medium">{l}</p>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] uppercase font-semibold text-slate-400">Scope</p>
                <p className="text-2xl font-bold text-slate-800 mt-0.5">{p.batches || 0} <span className="text-sm text-slate-400 font-normal">batches</span></p>
                <p className="text-[11px] text-slate-500">{p.trainers || 0} trainers · {p.students || 0} students</p>
                {data?.meta?.total_universe_batches && data.meta.total_universe_batches !== (p.batches || 0) && (
                  <p className="text-[10px] text-slate-400 mt-0.5">of {data.meta.total_universe_batches} in window</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase font-semibold text-slate-400">Invoice value</p>
                <p className="text-2xl font-bold text-slate-800 mt-0.5">{inr(p.gross)}</p>
                <p className="text-[11px]"><span className="text-emerald-700 font-medium">Paid {inr(p.paid)}</span> · <span className="text-rose-700 font-medium">Due {inr(p.due)}</span></p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-semibold text-slate-400 flex items-center gap-1">
                  Audit adjustments
                  <button onClick={() => setAuditOpen(true)} className="text-violet-600 hover:underline text-[9px] normal-case font-medium" data-testid="nto-audit-view">View →</button>
                </p>
                <p className="text-2xl font-bold text-amber-700 mt-0.5">{inr((p.credit_note || 0) + (p.write_off || 0))}</p>
                <p className="text-[11px] text-amber-700">CN {inr(p.credit_note)} · WO {inr(p.write_off)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-semibold text-slate-400">Overdue running</p>
                <p className={`text-2xl font-bold mt-0.5 ${p.overdue > 0 ? 'text-rose-600' : 'text-slate-800'}`}>{p.overdue || 0}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-rose-400" /> past end-date + {data?.meta?.grace_days || 7}d grace</p>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* TABS */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        {[['trainer', 'By Trainer', Users], ['category', 'By Category', Sparkles]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold border-b-2 transition-colors ${tab === k ? 'text-violet-700 border-violet-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}
            data-testid={`nto-tab-${k}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {/* TABLES */}
      {tab === 'trainer' && (
        <Card className="border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-slate-500">
                  <Th k="trainer_name">Trainer</Th>
                  <Th k="batch_count" align="right">Batches</Th>
                  <Th k="overdue_batches" align="right">Overdue</Th>
                  <Th k="students" align="right">Students</Th>
                  <th className="px-3 py-2 font-medium">State mix</th>
                  <Th k="gross" align="right">Gross</Th>
                  <Th k="paid" align="right">Paid</Th>
                  <Th k="due" align="right">Due</Th>
                  <Th k="attendance" align="right">Att%</Th>
                  <Th k="pdp" align="right">PDP%</Th>
                  <Th k="placement" align="right">Ready%</Th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {byTrainer.length === 0 && !loading && (
                  <tr><td colSpan={12} className="p-8 text-center text-slate-400 text-[12px]">No batches match your filters. Reset or widen the timeline.</td></tr>
                )}
                {byTrainer.map(r => (
                  <tr key={r.trainer_name} onClick={() => setSelectedTrainer(r)}
                    className="border-b border-slate-100 hover:bg-violet-50/60 cursor-pointer" data-testid={`nto-trainer-row-${r.trainer_name}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-800">{r.trainer_name}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-slate-700">{r.batch_count}</td>
                    <td className="px-3 py-2.5 text-right">
                      {(r.overdue_batches || 0) > 0
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold text-[11px]" title={`${r.overdue_batches} batch${r.overdue_batches>1?'es':''} past end-date + 7d grace as of ${data?.meta?.today}`}>
                            <AlertTriangle className="w-3 h-3" />{r.overdue_batches}
                          </span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{r.students}</td>
                    <td className="px-3 py-2.5"><StateStrip counts={r.counts} /></td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{inr(r.gross)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700">{inr(r.paid)}</td>
                    <td className="px-3 py-2.5 text-right text-rose-700">{inr(r.due)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.attendance)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.pdp)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.placement)}</td>
                    <td className="px-3 py-2.5 text-slate-300"><ChevronRight className="w-3.5 h-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'category' && (
        <Card className="border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-slate-500">
                  <Th k="branch">Category</Th>
                  <Th k="batch_count" align="right">Batches</Th>
                  <Th k="trainers" align="right">Trainers</Th>
                  <Th k="students" align="right">Students</Th>
                  <th className="px-3 py-2 font-medium">State mix</th>
                  <Th k="gross" align="right">Gross</Th>
                  <Th k="paid" align="right">Paid</Th>
                  <Th k="due" align="right">Due</Th>
                  <Th k="attendance" align="right">Att%</Th>
                  <Th k="pdp" align="right">PDP%</Th>
                  <Th k="placement" align="right">Ready%</Th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {byCategory.length === 0 && !loading && (
                  <tr><td colSpan={12} className="p-8 text-center text-slate-400 text-[12px]">No batches match your filters. Reset or widen the timeline.</td></tr>
                )}
                {byCategory.map(r => (
                  <tr key={r.branch} onClick={() => setSelectedCategory(r)}
                    className="border-b border-slate-100 hover:bg-violet-50/60 cursor-pointer" data-testid={`nto-cat-row-${r.branch}`}>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{r.branch}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-slate-700">{r.batch_count}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{r.trainers}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{r.students}</td>
                    <td className="px-3 py-2.5"><StateStrip counts={r.counts} /></td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{inr(r.gross)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700">{inr(r.paid)}</td>
                    <td className="px-3 py-2.5 text-right text-rose-700">{inr(r.due)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.attendance)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.pdp)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{pct(r.placement)}</td>
                    <td className="px-3 py-2.5 text-slate-300"><ChevronRight className="w-3.5 h-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal chain */}
      {selectedTrainer && (
        <TrainerModal trainer={selectedTrainer.trainer_name} batches={selectedTrainer.batches} kpi={selectedTrainer}
          open={!!selectedTrainer} onClose={() => setSelectedTrainer(null)} />
      )}
      {selectedCategory && (
        <CategoryModal category={selectedCategory.branch} batches={selectedCategory.batches} kpi={selectedCategory}
          open={!!selectedCategory} onClose={() => setSelectedCategory(null)} />
      )}
      <AuditModal audit={data?.audit} open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}
